/**
 * ComplianceContext - TASK-228 + TASK-231
 * Single source of truth for all compliance-related client data
 *
 * Enables shared data across forms (KYC, Risk Profile, Client Profile, Form A)
 * with multi-source ingestion support (document, transcription, API, manual).
 *
 * TASK-231: Now uses dedicated mapper functions from services/compliance/
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { toast } from 'sonner';
import { useDebounce } from '../hooks/useDebounce';
import kycService from '../services/kycService';
import { riskProfileAPI } from '../services/riskProfileService';
import { clientService } from '../services/clientService';
import type {
  ClientComplianceData,
  DataSource,
  TrackedField,
  FormType,
  FormConfig,
  SharedPersonalData,
  SharedContactData,
  SharedEmploymentData,
  SharedFinancialData,
  SharedTaxData,
  SharedPEPData,
} from '../types/clientCompliance';
import {
  createTrackedField as createTracked,
  createDataSource as createSource,
  resolveConflict as resolve,
  getFieldSection as getSection,
} from '../types/clientCompliance';

// TASK-231: Import mapper functions from dedicated compliance service
import {
  mapToKYC,
  mapFromKYC,
  mapToRiskProfile,
  mapFromRiskProfile,
  mapToClientProfile,
  mapFromClientProfile,
  mapToFormA,
  mapFromFormA,
  mapExtractionToAllForms,
} from '../services/compliance';
import type { ExtractionResult, AllFormsData } from '../services/compliance';

// ============================================================================
// CONTEXT VALUE INTERFACE
// ============================================================================

interface ComplianceContextValue {
  // State
  data: ClientComplianceData | null;
  loading: boolean;
  error: Error | null;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  version: number;

  // Client identification
  clientId: string;

  // Data operations
  loadClientCompliance: (clientId: string) => Promise<void>;
  updateField: <S extends keyof ClientComplianceData>(
    section: S,
    field: string,
    value: unknown,
    source: DataSource
  ) => void;

  // Multi-source ingestion
  ingestFromDocument: (
    data: Record<string, unknown>,
    source: Omit<DataSource, 'type'>
  ) => void;
  ingestFromTranscription: (
    data: Record<string, unknown>,
    source: Omit<DataSource, 'type'>
  ) => void;
  ingestFromAPI: (
    data: Record<string, unknown>,
    source: Omit<DataSource, 'type'>
  ) => void;
  ingestData: (
    data: Record<string, unknown>,
    source: DataSource,
    formType?: FormType
  ) => void;

  // TASK-231: Document extraction ingestion
  ingestFromExtraction: (extraction: ExtractionResult) => {
    compliance: Partial<ClientComplianceData>;
    forms: AllFormsData;
  };

  // TASK-232: Direct raw preview data ingestion (bypasses ExtractionResult transformation)
  ingestFromRawPreviewData: (
    rawKycData: Record<string, unknown> | undefined,
    rawRiskData: Record<string, unknown> | undefined,
    sourceName: string
  ) => void;

  // Form operations
  getFormData: <T>(formType: FormType) => T;
  saveFormSection: (
    formType: FormType,
    section: string,
    data: unknown
  ) => Promise<void>;
  getFormConfig: (formType: FormType) => FormConfig | undefined;

  // Field operations
  getFieldHistory: (section: string, field: string) => DataSource[];
  verifyField: (section: string, field: string, userId: string) => void;
  hasConflicts: (section: string, field: string) => boolean;

  // Status operations
  refreshScreeningStatus: () => Promise<void>;
  recalculateRiskRating: () => Promise<void>;

  // Save operations
  saveProgress: (showNotification?: boolean) => Promise<void>;
}

// ============================================================================
// FORM REGISTRY
// ============================================================================

/**
 * FORM_REGISTRY - TASK-231 Updated
 *
 * Now uses dedicated mapper functions from services/compliance/
 * for bidirectional mapping between shared data and form-specific formats.
 */
const FORM_REGISTRY: Partial<Record<FormType, FormConfig>> = {
  kyc: {
    id: 'kyc',
    name: 'KYC Form',
    description: 'Know Your Customer compliance form',
    version: '1.0',
    requiredSections: ['personal', 'contact', 'employment', 'tax', 'pep'],
    mapFromShared: (data: ClientComplianceData) => mapToKYC(data),
    mapToShared: (formData: unknown, source: DataSource) => mapFromKYC(formData as any, source),
  },
  riskProfile: {
    id: 'riskProfile',
    name: 'Risk Profile',
    description: 'Investment risk assessment questionnaire',
    version: '1.0',
    requiredSections: ['personal', 'financial'],
    mapFromShared: (data: ClientComplianceData) => mapToRiskProfile(data),
    mapToShared: (formData: unknown, source: DataSource) => mapFromRiskProfile(formData as any, source),
  },
  clientProfile: {
    id: 'clientProfile',
    name: 'Client Profile',
    description: 'Custodian bank account opening form',
    version: '1.0',
    requiredSections: ['personal', 'contact', 'tax', 'beneficialOwners'],
    mapFromShared: (data: ClientComplianceData) => mapToClientProfile(data),
    mapToShared: (formData: unknown, source: DataSource) => mapFromClientProfile(formData as any, source),
  },
  formA: {
    id: 'formA',
    name: 'Form A',
    description: 'Beneficial owner declaration form',
    version: '1.0',
    requiredSections: ['personal', 'contact', 'beneficialOwners', 'pep'],
    mapFromShared: (data: ClientComplianceData) => mapToFormA(data),
    mapToShared: (formData: unknown, source: DataSource) => mapFromFormA(formData as any, source),
  },
};

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const ComplianceContext = createContext<ComplianceContextValue | undefined>(
  undefined
);

// ============================================================================
// PROVIDER PROPS
// ============================================================================

interface ComplianceProviderProps {
  children: ReactNode;
  clientId: string;
  initialData?: Partial<ClientComplianceData>;
}

// ============================================================================
// SECTION COMPLETION HELPERS - TASK-230
// ============================================================================

/**
 * Calculate KYC form completion status from KYC data
 * Simple section presence check for status display
 */
function calculateKYCCompletion(kycData: any): {
  completedSections: number;
  totalSections: number;
  completedSectionNames: string[];
} {
  if (!kycData) {
    return { completedSections: 0, totalSections: 8, completedSectionNames: [] };
  }

  // Simple section checks - a section is complete if it has key data
  const sectionChecks = [
    { name: 'Personal', complete: !!(kycData.first_name || kycData.personalInfo?.firstName) },
    { name: 'Contact', complete: !!(kycData.phone_primary || kycData.email_primary || kycData.contactInfo?.phonePrimary) },
    { name: 'Employment', complete: !!(kycData.employment_status || kycData.employmentInfo?.employmentStatus) },
    { name: 'Tax', complete: !!(kycData.tax_residence || kycData.taxInfo?.taxResidence) },
    { name: 'PEP', complete: kycData.pep_status !== undefined || kycData.pepStatus !== undefined },
    { name: 'Wealth', complete: (kycData.wealth_sources?.length > 0 || kycData.wealthSources?.length > 0) },
    { name: 'Income', complete: (kycData.income_sources?.length > 0 || kycData.incomeSources?.length > 0) },
    { name: 'Documents', complete: !!(kycData.documents?.length > 0) },
  ];

  const completedSectionNames = sectionChecks.filter(s => s.complete).map(s => s.name);

  return {
    completedSections: completedSectionNames.length,
    totalSections: 8,
    completedSectionNames,
  };
}

/**
 * Calculate Risk Profile form completion status
 */
function calculateRiskProfileCompletion(riskData: any): {
  completedSections: number;
  totalSections: number;
  completedSectionNames: string[];
} {
  if (!riskData?.risk_profile) {
    return { completedSections: 0, totalSections: 10, completedSectionNames: [] };
  }

  const profile = riskData.risk_profile;
  // TASK-654: the stored profile is camelCase (see RiskProfileTab REQUIRED_FIELDS);
  // this function read snake_case keys and silently scored every real profile 0/10
  // — one of the audit's contradictory-count roots. Accept camelCase first and
  // keep the snake_case fallback for any legacy rows.
  const sectionChecks = [
    {
      name: 'Personal',
      complete: !!(
        (profile.section1?.firstName || profile.section1?.full_name) &&
        (profile.section1?.dateOfBirth || profile.section1?.date_of_birth)
      ),
    },
    { name: 'Knowledge', complete: !!(profile.section2?.educationLevel || profile.section2?.education_level) },
    {
      name: 'Financial',
      complete: !!(
        profile.section3?.portfolioAmountToManage || profile.section3?.portfolio?.amount_to_manage
      ),
    },
    { name: 'Objectives', complete: !!(profile.section4?.timeHorizon || profile.section4?.time_horizon) },
    { name: 'Risk', complete: !!(profile.section5?.riskAttitude || profile.section5?.risk_attitude) },
    { name: 'Profile', complete: !!(profile.section6?.determinedProfile || profile.section6?.determined_profile) },
    { name: 'Strategy', complete: !!(profile.section7?.strategyName || profile.section7?.strategy_name) },
    { name: 'Declarations', complete: !!(profile.section8?.declarations?.length > 0) },
    {
      name: 'Signatures',
      complete: !!(
        profile.section9?.clientSignatureSigned || profile.section9?.client_signature?.signed
      ),
    },
    { name: 'Review', complete: !!(profile.section10?.reviewFrequency || profile.section10?.review_frequency) },
  ];

  const completedSectionNames = sectionChecks.filter(s => s.complete).map(s => s.name);

  return {
    completedSections: completedSectionNames.length,
    totalSections: 10,
    completedSectionNames,
  };
}

// ============================================================================
// TRANSFORM FUNCTIONS
// ============================================================================

/**
 * Transform KYC data from backend to ClientComplianceData format
 */
function transformKYCToCompliance(
  kycData: any,
  clientId: string
): Partial<ClientComplianceData> {
  if (!kycData) return {};

  const source = createSource('inherited', `kyc_${clientId}`, 'KYC Profile', 1.0);

  return {
    personal: {
      firstName: kycData.personalInfo?.firstName
        ? createTracked(kycData.personalInfo.firstName, source)
        : undefined,
      lastName: kycData.personalInfo?.lastName
        ? createTracked(kycData.personalInfo.lastName, source)
        : undefined,
      dateOfBirth: kycData.personalInfo?.dateOfBirth
        ? createTracked(kycData.personalInfo.dateOfBirth, source)
        : undefined,
      nationalities: kycData.personalInfo?.nationalities
        ? createTracked(kycData.personalInfo.nationalities, source)
        : undefined,
      placeOfBirth: kycData.personalInfo?.placeOfBirth
        ? createTracked(kycData.personalInfo.placeOfBirth, source)
        : undefined,
      gender: kycData.personalInfo?.gender
        ? createTracked(kycData.personalInfo.gender, source)
        : undefined,
      maritalStatus: kycData.personalInfo?.maritalStatus
        ? createTracked(kycData.personalInfo.maritalStatus, source)
        : undefined,
      passportNumber: kycData.personalInfo?.passportNumber
        ? createTracked(kycData.personalInfo.passportNumber, source)
        : undefined,
      nationalId: kycData.personalInfo?.nationalId
        ? createTracked(kycData.personalInfo.nationalId, source)
        : undefined,
    },
    contact: {
      phonePrimary: kycData.contactInfo?.phonePrimary
        ? createTracked(kycData.contactInfo.phonePrimary, source)
        : undefined,
      phoneSecondary: kycData.contactInfo?.phoneSecondary
        ? createTracked(kycData.contactInfo.phoneSecondary, source)
        : undefined,
      emailPrimary: kycData.contactInfo?.emailPrimary
        ? createTracked(kycData.contactInfo.emailPrimary, source)
        : undefined,
      emailSecondary: kycData.contactInfo?.emailSecondary
        ? createTracked(kycData.contactInfo.emailSecondary, source)
        : undefined,
      residentialAddress: kycData.contactInfo?.residentialAddress
        ? createTracked(kycData.contactInfo.residentialAddress, source)
        : undefined,
      preferredContactMethod: kycData.contactInfo?.preferredContactMethod
        ? createTracked(kycData.contactInfo.preferredContactMethod, source)
        : undefined,
    },
    employment: {
      employmentStatus: kycData.employmentInfo?.employmentStatus
        ? createTracked(kycData.employmentInfo.employmentStatus, source)
        : undefined,
      occupation: kycData.employmentInfo?.occupation
        ? createTracked(kycData.employmentInfo.occupation, source)
        : undefined,
      employerName: kycData.employmentInfo?.employerName
        ? createTracked(kycData.employmentInfo.employerName, source)
        : undefined,
      employmentIndustry: kycData.employmentInfo?.employmentIndustry
        ? createTracked(kycData.employmentInfo.employmentIndustry, source)
        : undefined,
      yearsEmployed: kycData.employmentInfo?.yearsEmployed
        ? createTracked(kycData.employmentInfo.yearsEmployed, source)
        : undefined,
    },
    tax: {
      taxResidence: kycData.taxInfo?.taxResidence
        ? createTracked(kycData.taxInfo.taxResidence, source)
        : undefined,
      taxId: kycData.taxInfo?.taxId
        ? createTracked(kycData.taxInfo.taxId, source)
        : undefined,
      usPerson: kycData.taxInfo?.usPerson !== undefined
        ? createTracked(kycData.taxInfo.usPerson, source)
        : undefined,
      fatcaStatus: kycData.taxInfo?.fatcaStatus
        ? createTracked(kycData.taxInfo.fatcaStatus, source)
        : undefined,
    },
    pep: {
      isPep: kycData.pepStatus?.isPEP !== undefined
        ? createTracked(kycData.pepStatus.isPEP, source)
        : undefined,
      isRca: kycData.pepStatus?.isRCA !== undefined
        ? createTracked(kycData.pepStatus.isRCA, source)
        : undefined,
      pepType: kycData.pepStatus?.pepType
        ? createTracked(kycData.pepStatus.pepType, source)
        : undefined,
      pepPosition: kycData.pepStatus?.positions?.[0]?.title
        ? createTracked(kycData.pepStatus.positions[0].title, source)
        : undefined,
      pepCountry: kycData.pepStatus?.countries?.[0]
        ? createTracked(kycData.pepStatus.countries[0], source)
        : undefined,
    },
  };
}

/**
 * Transform Risk Profile data to ClientComplianceData format
 */
function transformRiskProfileToCompliance(
  riskData: any,
  clientId: string
): Partial<ClientComplianceData> {
  if (!riskData?.risk_profile) return {};

  const source = createSource(
    'inherited',
    `risk_${clientId}`,
    'Risk Profile',
    1.0
  );
  const profile = riskData.risk_profile;

  return {
    financial: {
      totalAssets: profile.section3?.assets?.total_assets
        ? createTracked(profile.section3.assets.total_assets, source)
        : undefined,
      annualIncome: profile.section3?.income?.total_annual_net
        ? createTracked(profile.section3.income.total_annual_net, source)
        : undefined,
      liquidAssets: profile.section3?.assets?.cash_deposits
        ? createTracked(profile.section3.assets.cash_deposits, source)
        : undefined,
    },
  };
}

/**
 * Transform Client data to ClientComplianceData format
 */
function transformClientToCompliance(
  clientData: any,
  clientId: string
): Partial<ClientComplianceData> {
  if (!clientData) return {};

  const source = createSource(
    'inherited',
    `client_${clientId}`,
    'Client Record',
    1.0
  );

  // Parse name into first/last
  const nameParts = (clientData.name || '').split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    personal: {
      firstName: firstName ? createTracked(firstName, source) : undefined,
      lastName: lastName ? createTracked(lastName, source) : undefined,
    },
    contact: {
      emailPrimary: clientData.email
        ? createTracked(clientData.email, source)
        : undefined,
      phonePrimary: clientData.phone
        ? createTracked(clientData.phone, source)
        : undefined,
    },
  };
}

/**
 * Merge compliance data from multiple sources
 */
function mergeComplianceData(
  ...sources: Partial<ClientComplianceData>[]
): Partial<ClientComplianceData> {
  const result: Partial<ClientComplianceData> = {};

  for (const source of sources) {
    for (const [sectionKey, sectionValue] of Object.entries(source)) {
      if (sectionValue && typeof sectionValue === 'object') {
        if (!result[sectionKey as keyof ClientComplianceData]) {
          (result as any)[sectionKey] = {};
        }
        for (const [fieldKey, fieldValue] of Object.entries(sectionValue)) {
          if (fieldValue !== undefined) {
            const existing = (result as any)[sectionKey]?.[fieldKey];
            if (existing && fieldValue) {
              // Resolve conflict using priority rules
              (result as any)[sectionKey][fieldKey] = resolve(
                existing as TrackedField<unknown>,
                fieldValue as TrackedField<unknown>
              );
            } else {
              (result as any)[sectionKey][fieldKey] = fieldValue;
            }
          }
        }
      }
    }
  }

  return result;
}

// ============================================================================
// PROVIDER IMPLEMENTATION
// ============================================================================

export const ComplianceProvider: React.FC<ComplianceProviderProps> = ({
  children,
  clientId,
  initialData,
}) => {
  // State declarations
  const [data, setData] = useState<ClientComplianceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [version, setVersion] = useState(1);

  // Debounced data for auto-save
  const debouncedData = useDebounce(data, 3000);

  // Load client compliance data from all services
  const loadClientCompliance = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      // Aggregate from existing services in parallel
      const [kycData, riskData, clientData, suitability] = await Promise.all([
        kycService.profiles.get(id).catch(() => null),
        riskProfileAPI.get(id).catch(() => null),
        clientService.getClient(id).catch(() => null),
        // TASK-828: the SUITABILITY state, from the server's own predicate.
        //
        // This page computed `calculateRiskProfileCompletion` over the form's filled sections
        // and labelled the result "Completed", while the mandatory `reprofile_due` alert said
        // "Risk profile incomplete". Both were honestly derived; neither said which question
        // it answered, and an advisor told a suitability obligation is outstanding who then
        // reads "Completed" will reasonably conclude the alert is noise. Teaching advisors to
        // ignore the mandatory band is the worst available outcome.
        //
        // `GET /clients/{id}/compliance-summary` already returned this — the projected
        // `project_determined_profile`, the same predicate the detector and the agentic tool
        // read. It was simply never called from here.
        clientService.getComplianceSummary(id).catch(() => null),
      ]);

      // Transform each source to compliance format
      const kycCompliance = transformKYCToCompliance(kycData, id);
      const riskCompliance = transformRiskProfileToCompliance(riskData, id);
      const clientCompliance = transformClientToCompliance(clientData, id);

      // Merge all sources with conflict resolution
      const mergedData = mergeComplianceData(
        clientCompliance, // Lowest priority
        riskCompliance,
        kycCompliance // Highest priority
      );

      // Create complete compliance data structure
      const complianceData: ClientComplianceData = {
        clientId: id,
        personal: (mergedData.personal as SharedPersonalData) || {},
        contact: (mergedData.contact as SharedContactData) || {},
        employment: (mergedData.employment as SharedEmploymentData) || {},
        financial: (mergedData.financial as SharedFinancialData) || {},
        tax: (mergedData.tax as SharedTaxData) || {},
        pep: (mergedData.pep as SharedPEPData) || {},
        beneficialOwners: [],
        forms: {
          kyc: kycData
            ? (() => {
                const kycCompletion = calculateKYCCompletion(kycData);
                return {
                  status: kycData.status === 'approved' ? 'completed' as const :
                         kycData.status === 'submitted' ? 'submitted' as const :
                         kycData.status === 'changes_requested' ? 'changes_requested' as const :
                         kycCompletion.completedSections === 0 ? 'not_started' as const : 'in_progress' as const,
                  completedSections: kycCompletion.completedSections,
                  totalSections: kycCompletion.totalSections,
                  lastUpdated: kycData.metadata?.updatedAt || kycData.updated_at,
                  completedSectionNames: kycCompletion.completedSectionNames,
                };
              })()
            : undefined,
          riskProfile: riskData
            ? (() => {
                const riskCompletion = calculateRiskProfileCompletion(riskData);
                return {
                  status: riskData.status === 'completed' ? 'completed' as const :
                         riskCompletion.completedSections === 0 ? 'not_started' as const : 'in_progress' as const,
                  completedSections: riskCompletion.completedSections,
                  totalSections: riskCompletion.totalSections,
                  lastUpdated: riskData.last_updated || undefined,
                  completedSectionNames: riskCompletion.completedSectionNames,
                  // TASK-828: FORM completion above, SUITABILITY state here — two facts, two
                  // names. `determinedRiskProfile === null` means no determined category, so
                  // no suitability assessment can be made against this record however many
                  // sections are filled. `undefined` means the summary could not be read, and
                  // is deliberately distinct from "there is none".
                  determinedRiskProfile: suitability
                    ? suitability.risk_profile ?? null
                    : undefined,
                };
              })()
            : undefined,
        },
        screening: {},
        metadata: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        },
      };

      setData(complianceData);
      setVersion(1);
    } catch (err) {
      setError(err as Error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a single field with source tracking
  const updateField = useCallback(
    <S extends keyof ClientComplianceData>(
      section: S,
      field: string,
      value: unknown,
      source: DataSource
    ) => {
      setData((prev) => {
        if (!prev) return prev;

        const sectionData = prev[section] as Record<string, TrackedField<unknown>> | undefined;
        const existingField = sectionData?.[field];

        // Create new tracked field, preserving history
        const newField = createTracked(
          value,
          source,
          existingField?.history
            ? [...existingField.history, existingField.source]
            : existingField?.source
              ? [existingField.source]
              : []
        );

        // Apply conflict resolution if existing field exists
        const finalField = existingField ? resolve(existingField, newField) : newField;

        return {
          ...prev,
          [section]: {
            ...(sectionData || {}),
            [field]: finalField,
          },
          metadata: {
            ...prev.metadata,
            updatedAt: new Date().toISOString(),
          },
        };
      });
      setIsDirty(true);
    },
    []
  );

  // Generic data ingestion
  const ingestData = useCallback(
    (
      extractedData: Record<string, unknown>,
      source: DataSource,
      _formType?: FormType
    ) => {
      // Map extracted data to shared data sections using canonical field names
      Object.entries(extractedData).forEach(([field, value]) => {
        if (value !== undefined && value !== null) {
          const section = getSection(field);
          if (section) {
            updateField(section, field, value, source);
          }
        }
      });
    },
    [updateField]
  );

  // Specialized ingestion methods
  const ingestFromDocument = useCallback(
    (extractedData: Record<string, unknown>, source: Omit<DataSource, 'type'>) => {
      ingestData(extractedData, { ...source, type: 'document' } as DataSource);
    },
    [ingestData]
  );

  const ingestFromTranscription = useCallback(
    (extractedData: Record<string, unknown>, source: Omit<DataSource, 'type'>) => {
      ingestData(extractedData, { ...source, type: 'transcription' } as DataSource);
    },
    [ingestData]
  );

  const ingestFromAPI = useCallback(
    (extractedData: Record<string, unknown>, source: Omit<DataSource, 'type'>) => {
      ingestData(extractedData, { ...source, type: 'api' } as DataSource);
    },
    [ingestData]
  );

  /**
   * TASK-231: Ingest extraction result from RAG document processing
   * Maps extraction to compliance data and updates state, returning
   * both the compliance data and pre-mapped form data.
   */
  const ingestFromExtraction = useCallback(
    (extraction: ExtractionResult): { compliance: Partial<ClientComplianceData>; forms: AllFormsData } => {
      const result = mapExtractionToAllForms(extraction);

      // Merge extracted compliance data with existing data
      setData((prev) => {
        if (!prev) {
          // No existing data - create fresh from extraction
          return {
            clientId: extraction.upload_id,
            personal: result.compliance.personal || {},
            contact: result.compliance.contact || {},
            employment: result.compliance.employment || {},
            financial: result.compliance.financial || {},
            tax: result.compliance.tax || {},
            pep: result.compliance.pep || {},
            beneficialOwners: result.compliance.beneficialOwners || [],
            forms: {},
            screening: {},
            metadata: result.compliance.metadata || {
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              version: 1,
            },
          } as ClientComplianceData;
        }

        // Merge with existing data using conflict resolution
        const merged = mergeComplianceData(prev, result.compliance);
        return {
          ...prev,
          ...merged,
          metadata: {
            ...prev.metadata,
            updatedAt: new Date().toISOString(),
          },
        } as ClientComplianceData;
      });

      setIsDirty(true);

      toast.success('Document data extracted and mapped to forms', {
        duration: 3000,
        position: 'bottom-right',
      });

      return result;
    },
    []
  );

  /**
   * TASK-232: Ingest raw preview data directly from backend extraction
   *
   * This bypasses the ExtractionResult transformation which loses data.
   * The backend returns kyc_data and risk_profile_data with {value, confidence} wrappers
   * for each field - we map those directly to ClientComplianceData.
   */
  const ingestFromRawPreviewData = useCallback(
    (
      rawKycData: Record<string, unknown> | undefined,
      _rawRiskData: Record<string, unknown> | undefined,
      sourceName: string
    ) => {
      console.log('🔄 TASK-232: Ingesting raw preview data directly');

      // Helper to unwrap {value, confidence} structure
      const unwrap = <T,>(field: unknown): T | undefined => {
        if (field === undefined || field === null) return undefined;
        if (typeof field === 'object' && (field as any).value !== undefined) {
          return (field as any).value as T;
        }
        return field as T;
      };

      // Helper to get confidence from {value, confidence} wrapper
      const getConf = (field: unknown): number => {
        if (field && typeof field === 'object' && typeof (field as any).confidence === 'number') {
          return (field as any).confidence;
        }
        return 0.5;
      };

      // Create source for tracked fields
      const source = createSource(
        'document',
        `raw_preview_${Date.now()}`,
        sourceName,
        0.8,
        undefined,
        'system'
      );

      // Helper to create tracked field from raw {value, confidence} wrapper
      const createField = <T,>(field: unknown): TrackedField<T> | undefined => {
        const value = unwrap<T>(field);
        if (value === undefined || value === null || value === '') return undefined;
        return createTracked(value, { ...source, confidence: getConf(field) });
      };

      // Map raw KYC data to compliance sections
      const kyc = rawKycData || {};
      const personalInfo = (kyc.personalInfo || {}) as Record<string, unknown>;
      const contactInfo = (kyc.contactInfo || {}) as Record<string, unknown>;
      const employmentInfo = (kyc.employmentInfo || {}) as Record<string, unknown>;
      const wealthSummary = (kyc.wealthSummary || {}) as Record<string, unknown>;
      const taxInfo = (kyc.taxInfo || {}) as Record<string, unknown>;
      const pepStatus = (kyc.pepStatus || {}) as Record<string, unknown>;
      const address = (kyc.address || {}) as Record<string, unknown>;

      // Build personal data
      const personal: SharedPersonalData = {
        firstName: createField<string>(personalInfo.firstName),
        middleName: createField<string>(personalInfo.middleName),
        lastName: createField<string>(personalInfo.lastName),
        dateOfBirth: createField<string>(personalInfo.dateOfBirth),
        placeOfBirth: createField<string>(personalInfo.placeOfBirth),
        gender: createField<'male' | 'female' | 'other' | 'prefer_not_to_say'>(personalInfo.gender),
        maritalStatus: createField<'single' | 'married' | 'divorced' | 'widowed' | 'separated' | 'domestic_partnership'>(personalInfo.maritalStatus),
        nationalities: createField<string[]>(personalInfo.nationalities),
        languages: createField<string[]>(personalInfo.languages),
        passportNumber: createField<string>(personalInfo.passportNumber),
        nationalId: createField<string>(personalInfo.nationalId),
      };

      // Build contact data
      const contact: SharedContactData = {
        phonePrimary: createField<string>(contactInfo.phonePrimary || contactInfo.mobilePhone),
        phoneSecondary: createField<string>(contactInfo.phoneSecondary),
        emailPrimary: createField<string>(contactInfo.emailPrimary || contactInfo.email),
        emailSecondary: createField<string>(contactInfo.emailSecondary),
        preferredContactMethod: createField<'email' | 'phone' | 'whatsapp' | 'video_call' | 'in_person'>(contactInfo.preferredContactMethod),
        // Address from either contactInfo.residentialAddress or separate address section
        residentialAddress: address.street || address.city ? createField({
          street: unwrap<string>(address.street) || '',
          city: unwrap<string>(address.city) || '',
          postalCode: unwrap<string>(address.postalCode) || '',
          canton: unwrap<string>(address.canton),
          country: unwrap<string>(address.country) || '',
        }) : undefined,
      };

      // Build employment data
      const employment: SharedEmploymentData = {
        employmentStatus: createField<'employed' | 'selfEmployed' | 'unemployed' | 'retired' | 'student' | 'other'>(
          employmentInfo.employmentStatus || employmentInfo.status
        ),
        occupation: createField<string>(employmentInfo.occupation),
        employerName: createField<string>(employmentInfo.employerName || employmentInfo.employer),
        employmentIndustry: createField<string>(employmentInfo.industry || employmentInfo.employmentIndustry),
        yearsEmployed: createField<number>(employmentInfo.yearsEmployed),
      };

      // Build financial data from wealthSummary
      const financial: SharedFinancialData = {
        netWorth: createField<number>(wealthSummary.netWorth),
        totalAssets: createField<number>(wealthSummary.totalAssets || wealthSummary.netWorth),
        liquidAssets: createField<number>(wealthSummary.liquidNetWorth),
        annualIncome: createField<number>(employmentInfo.annualIncome),
        sourceOfWealth: createField<string>(wealthSummary.sourceOfWealth),
        primaryWealthSource: createField<string>(wealthSummary.primaryWealthSource),
        assetCurrency: createField<string>(wealthSummary.netWorthCurrency),
      };

      // Build tax data
      const tax: SharedTaxData = {
        taxResidence: createField<string>(taxInfo.taxResidence),
        taxId: createField<string>(taxInfo.taxId || taxInfo.tinNumber),
        usPerson: createField<boolean>(taxInfo.usPerson),
        fatcaStatus: createField<'compliant' | 'non_compliant' | 'pending' | 'exempt'>(taxInfo.fatcaStatus),
      };

      // Build PEP data
      // pepStatus can be either a direct object or wrapped in {value, confidence}
      const pepData = pepStatus.value ? (pepStatus.value as Record<string, unknown>) : pepStatus;
      const pep: SharedPEPData = {
        isPep: createField<boolean>(pepData.isPep || pepData.isPEP),
        isRca: createField<boolean>(pepData.isRca || pepData.isRCA),
        pepType: createField<'domestic' | 'foreign' | 'international'>(pepData.pepType),
        pepPosition: createField<string>(pepData.pepRole || (pepData.pepPositions as string[])?.[0]),
        pepCountry: createField<string>((pepData.pepCountries as string[])?.[0]),
      };

      // Log what we mapped
      console.log('📋 TASK-232: Mapped raw data to compliance:', {
        personal: Object.keys(personal).filter(k => personal[k as keyof SharedPersonalData]).length,
        contact: Object.keys(contact).filter(k => contact[k as keyof SharedContactData]).length,
        employment: Object.keys(employment).filter(k => employment[k as keyof SharedEmploymentData]).length,
        financial: Object.keys(financial).filter(k => financial[k as keyof SharedFinancialData]).length,
        tax: Object.keys(tax).filter(k => tax[k as keyof SharedTaxData]).length,
        pep: Object.keys(pep).filter(k => pep[k as keyof SharedPEPData]).length,
      });

      // Merge into state
      setData((prev) => {
        if (!prev) {
          return {
            clientId: clientId,
            personal,
            contact,
            employment,
            financial,
            tax,
            pep,
            beneficialOwners: [],
            forms: {},
            screening: {},
            metadata: {
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              version: 1,
            },
          } as ClientComplianceData;
        }

        // Merge with existing, keeping non-empty values
        const mergeSection = <T extends Record<string, unknown>>(existing: T, incoming: T): T => {
          const merged = { ...existing };
          for (const key of Object.keys(incoming)) {
            const incomingVal = incoming[key];
            const existingVal = existing[key];
            // Only overwrite if incoming has value and existing doesn't
            if (incomingVal && (!existingVal || !(existingVal as TrackedField<unknown>).value)) {
              (merged as Record<string, unknown>)[key] = incomingVal;
            }
          }
          return merged;
        };

        // Cast incoming data to Record<string, unknown> for merging
        const personalRecord = personal as unknown as Record<string, unknown>;
        const contactRecord = contact as unknown as Record<string, unknown>;
        const employmentRecord = employment as unknown as Record<string, unknown>;
        const financialRecord = financial as unknown as Record<string, unknown>;
        const taxRecord = tax as unknown as Record<string, unknown>;
        const pepRecord = pep as unknown as Record<string, unknown>;

        return {
          ...prev,
          personal: mergeSection(prev.personal || {} as Record<string, unknown>, personalRecord),
          contact: mergeSection(prev.contact || {} as Record<string, unknown>, contactRecord),
          employment: mergeSection(prev.employment || {} as Record<string, unknown>, employmentRecord),
          financial: mergeSection(prev.financial || {} as Record<string, unknown>, financialRecord),
          tax: mergeSection(prev.tax || {} as Record<string, unknown>, taxRecord),
          pep: mergeSection(prev.pep || {} as Record<string, unknown>, pepRecord),
          metadata: {
            ...prev.metadata,
            updatedAt: new Date().toISOString(),
          },
        } as ClientComplianceData;
      });

      setIsDirty(true);
      console.log('✅ TASK-232: Raw preview data ingestion complete');
    },
    [clientId]
  );

  // Get form-specific data view
  const getFormData = useCallback(
    <T,>(formType: FormType): T => {
      if (!data) return {} as T;
      const config = FORM_REGISTRY[formType];
      if (!config) return {} as T;
      return config.mapFromShared(data) as T;
    },
    [data]
  );

  // Get form configuration
  const getFormConfig = useCallback((formType: FormType): FormConfig | undefined => {
    return FORM_REGISTRY[formType];
  }, []);

  // Save form section (placeholder - integrates with individual form services)
  const saveFormSection = useCallback(
    async (_formType: FormType, _section: string, _sectionData: unknown) => {
      // This would integrate with individual form services
      // For now, just mark as dirty and let auto-save handle it
      setIsDirty(true);
    },
    []
  );

  // Get field history (audit trail)
  const getFieldHistory = useCallback(
    (section: string, field: string): DataSource[] => {
      if (!data) return [];
      const sectionData = data[section as keyof ClientComplianceData];
      if (typeof sectionData === 'object' && sectionData !== null) {
        const fieldData = (sectionData as Record<string, TrackedField<unknown>>)[field];
        return fieldData?.history || [];
      }
      return [];
    },
    [data]
  );

  // Verify a field (mark as manually verified)
  const verifyField = useCallback(
    (section: string, field: string, userId: string) => {
      setData((prev) => {
        if (!prev) return prev;

        const sectionData = prev[section as keyof ClientComplianceData];
        if (typeof sectionData !== 'object' || sectionData === null) return prev;

        const fieldData = (sectionData as Record<string, TrackedField<unknown>>)[field];
        if (!fieldData) return prev;

        return {
          ...prev,
          [section]: {
            ...(sectionData as Record<string, unknown>),
            [field]: {
              ...fieldData,
              isVerified: true,
              verifiedBy: userId,
              verifiedAt: new Date().toISOString(),
            },
          },
          metadata: {
            ...prev.metadata,
            updatedAt: new Date().toISOString(),
          },
        };
      });
      setIsDirty(true);
    },
    []
  );

  // Check for field conflicts (multiple sources with different values)
  const hasConflicts = useCallback(
    (section: string, field: string): boolean => {
      if (!data) return false;
      const sectionData = data[section as keyof ClientComplianceData];
      if (typeof sectionData === 'object' && sectionData !== null) {
        const fieldData = (sectionData as Record<string, TrackedField<unknown>>)[field];
        // Has conflicts if there's history with different values
        return (fieldData?.history?.length || 0) > 0;
      }
      return false;
    },
    [data]
  );

  // Refresh screening status (placeholder for integration)
  const refreshScreeningStatus = useCallback(async () => {
    // Would call screening service API
    toast.info('Screening status refresh not yet implemented');
  }, []);

  // Recalculate risk rating (placeholder for integration)
  const recalculateRiskRating = useCallback(async () => {
    // Would call risk calculation service
    toast.info('Risk rating recalculation not yet implemented');
  }, []);

  // Save progress to localStorage
  const saveProgress = useCallback(
    async (showNotification = true) => {
      if (!data) return;

      setIsSaving(true);
      try {
        const storageKey = `compliance_draft_${clientId}`;
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            data,
            savedAt: new Date().toISOString(),
            version,
          })
        );

        setLastSavedAt(new Date());
        setIsDirty(false);

        if (showNotification) {
          toast.success('Draft saved locally', {
            duration: 2000,
            position: 'bottom-right',
          });
        }
      } catch (err) {
        console.error('Failed to save draft:', err);
        if (showNotification) {
          toast.error('Failed to save draft');
        }
      } finally {
        setIsSaving(false);
      }
    },
    [clientId, data, version]
  );

  // Auto-save effect
  useEffect(() => {
    if (isDirty && !isSaving && debouncedData) {
      saveProgress(false);
    }
  }, [debouncedData, isDirty, isSaving, saveProgress]);

  // Load on mount
  useEffect(() => {
    if (initialData) {
      // Transform initial data to full compliance data structure
      const completeData: ClientComplianceData = {
        clientId,
        personal: (initialData.personal as SharedPersonalData) || {},
        contact: (initialData.contact as SharedContactData) || {},
        employment: (initialData.employment as SharedEmploymentData) || {},
        financial: (initialData.financial as SharedFinancialData) || {},
        tax: (initialData.tax as SharedTaxData) || {},
        pep: (initialData.pep as SharedPEPData) || {},
        beneficialOwners: initialData.beneficialOwners || [],
        forms: initialData.forms || {},
        screening: initialData.screening || {},
        riskRating: initialData.riskRating,
        metadata: initialData.metadata || {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        },
      };
      setData(completeData);
    } else {
      // Check for saved draft first
      const storageKey = `compliance_draft_${clientId}`;
      const savedDraft = localStorage.getItem(storageKey);

      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setData(parsed.data);
          setVersion(parsed.version || 1);
          toast.info('Draft loaded from local storage', {
            duration: 3000,
            position: 'bottom-right',
          });
        } catch (err) {
          console.error('Failed to load saved draft:', err);
          loadClientCompliance(clientId);
        }
      } else {
        loadClientCompliance(clientId);
      }
    }
  }, [clientId, initialData, loadClientCompliance]);

  // Context value
  const value: ComplianceContextValue = {
    data,
    loading,
    error,
    isDirty,
    isSaving,
    lastSavedAt,
    version,
    clientId,
    loadClientCompliance,
    updateField,
    ingestFromDocument,
    ingestFromTranscription,
    ingestFromAPI,
    ingestData,
    ingestFromExtraction, // TASK-231
    ingestFromRawPreviewData, // TASK-232
    getFormData,
    saveFormSection,
    getFormConfig,
    getFieldHistory,
    verifyField,
    hasConflicts,
    refreshScreeningStatus,
    recalculateRiskRating,
    saveProgress,
  };

  return (
    <ComplianceContext.Provider value={value}>
      {children}
    </ComplianceContext.Provider>
  );
};

// ============================================================================
// CONTEXT HOOK
// ============================================================================

/**
 * Hook to access the ComplianceContext
 * Must be used within a ComplianceProvider
 */
export function useComplianceContext(): ComplianceContextValue {
  const context = useContext(ComplianceContext);
  if (!context) {
    throw new Error(
      'useCompliance must be used within ComplianceProvider. ' +
        'Make sure <ComplianceProvider> wraps your component.'
    );
  }
  return context;
}

/**
 * Non-throwing accessor — returns undefined when rendered outside a ComplianceProvider
 * (e.g. summoned as a canvas widget). Use when the component can get its clientId elsewhere.
 */
export function useComplianceContextOptional(): ComplianceContextValue | undefined {
  return useContext(ComplianceContext);
}

// Export form registry for external use
export { FORM_REGISTRY };
